/**
 * Resident Summary Card Component
 *
 * Individual stat card displaying a count with label and icon.
 * Used in the Residents tab to show risk level distribution.
 */

import type { LucideIcon } from "lucide-react";

interface ResidentSummaryCardProps {
  label: string;
  count: number;
  icon: LucideIcon;
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  accentColor?: string;
  footer?: string;
  footerColor?: string;
}

export function ResidentSummaryCard({
  label,
  count,
  icon: Icon,
  backgroundColor,
  textColor,
  borderColor,
  accentColor,
  footer,
  footerColor = "#6B7280",
}: ResidentSummaryCardProps) {
  const iconAndCountColor = accentColor ?? textColor;

  return (
    <div
      className="flex-auto flex flex-row items-center justify-between px-3 rounded-md gap-3"
      style={{
        backgroundColor,
        border: `1px solid ${borderColor}`,
      }}
    >
      {/* LEFT: icon+label + footer stacked */}
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Icon
            size={18}
            strokeWidth={2}
            style={{ color: iconAndCountColor }}
          />
          <span
            className="text-sm font-bold uppercase whitespace-nowrap"
            style={{ color: textColor }}
          >
            {label}
          </span>
        </div>
        {footer && (
          <span
            className="text-xs whitespace-nowrap"
            style={{ color: footerColor }}
          >
            {footer}
          </span>
        )}
      </div>
      {/* RIGHT: count */}
      <span
        className="text-[2.25rem] font-bold leading-tight"
        style={{ color: iconAndCountColor }}
      >
        {count}
      </span>
    </div>
  );
}
